import l10n from '../../../../shared/strings';

const isEmpty = (value) => value === null || value === undefined || value === '';

const isValidUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const getMinMax = (schema) => ({
  min: schema.min ?? schema.validation?.min,
  max: schema.max ?? schema.validation?.max,
});

export const validateFieldValue = (type, value, schema = {}) => {
  const { required } = schema;
  const VALID = { isValid: true, errorMessage: null };
  const { validation } = l10n.PRODUCT_INFO;

  switch (type) {
  case 'int': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    const num = Number(value);
    if (!Number.isInteger(num) || String(value).includes('.')) {
      return { isValid: false, errorMessage: validation.enterWholeNumber };
    }
    const { min, max } = getMinMax(schema);
    if (min !== undefined && num < min) return { isValid: false, errorMessage: validation.minValue(min) };
    if (max !== undefined && num > max) return { isValid: false, errorMessage: validation.maxValue(max) };
    return VALID;
  }

  case 'decimal': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    const num = Number(value);
    if (Number.isNaN(num)) return { isValid: false, errorMessage: validation.enterValidNumber };
    const { min, max } = getMinMax(schema);
    if (min !== undefined && num < min) return { isValid: false, errorMessage: validation.minValue(min) };
    if (max !== undefined && num > max) return { isValid: false, errorMessage: validation.maxValue(max) };
    return VALID;
  }

  case 'rating': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    const num = Number(value);
    if (Number.isNaN(num)) {
      return { isValid: false, errorMessage: validation.enterValidNumber };
    }
    const min = schema.min;
    const max = schema.max;
    const hasMin = min != null;
    const hasMax = max != null;
    if (hasMin && hasMax) {
      if (num < min || num > max) {
        return { isValid: false, errorMessage: validation.ratingRange(min, max) };
      }
      return VALID;
    }
    if (hasMin && num < min) {
      return { isValid: false, errorMessage: validation.minValue(min) };
    }
    if (hasMax && num > max) {
      return { isValid: false, errorMessage: validation.maxValue(max) };
    }
    if (!hasMin && !hasMax && num <= 0) {
      return { isValid: false, errorMessage: validation.ratingPositive };
    }
    return VALID;
  }

  case 'url': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    if (!isValidUrl(String(value))) return { isValid: false, errorMessage: validation.enterValidUrl };
    return VALID;
  }

  case 'date': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return { isValid: false, errorMessage: validation.enterValidDate };
    return VALID;
  }

  case 'datetime': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    if (typeof value !== 'string' || !value.includes('T')) {
      return { isValid: false, errorMessage: validation.enterValidDateTime };
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return { isValid: false, errorMessage: validation.enterValidDateTime };
    return VALID;
  }

  case 'json': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    try {
      JSON.parse(value);
      return VALID;
    } catch {
      return { isValid: false, errorMessage: validation.enterValidJson };
    }
  }

  case 'money': {
    const amount = typeof value === 'object' ? (value?.value ?? '') : value;
    const currency = typeof value === 'object' ? (value?.currency ?? '') : '';
    const fieldIsEmpty = isEmpty(amount) && isEmpty(currency);
    if (required && fieldIsEmpty) return { isValid: false, errorMessage: validation.fieldRequired };
    if (fieldIsEmpty) return VALID;
    if (!isEmpty(amount)) {
      const num = Number(amount);
      if (Number.isNaN(num)) {
        return { isValid: false, errorMessage: validation.enterValidAmount };
      }
      if (num < 0) {
        return { isValid: false, errorMessage: validation.nonNegativeNumber };
      }
    }
    if (isEmpty(currency)) return { isValid: false, errorMessage: validation.selectCurrency };
    return VALID;
  }

  case 'weight':
  case 'dimension':
  case 'volume': {
    const measureValue = typeof value === 'object' ? (value?.value ?? '') : value;
    const unit = typeof value === 'object' ? (value?.unit ?? '') : '';
    const fieldIsEmpty = isEmpty(measureValue) && isEmpty(unit);
    if (required && fieldIsEmpty) return { isValid: false, errorMessage: validation.fieldRequired };
    if (fieldIsEmpty) return VALID;
    if (!isEmpty(measureValue)) {
      const num = Number(measureValue);
      if (Number.isNaN(num)) {
        return { isValid: false, errorMessage: validation.enterValidValue };
      }
      if (num < 0) {
        return { isValid: false, errorMessage: validation.nonNegativeNumber };
      }
    }
    if (isEmpty(unit)) return { isValid: false, errorMessage: validation.selectUnit };
    return VALID;
  }

  case 'link': {
    const url = typeof value === 'object' ? (value?.url ?? '') : value;
    const title = typeof value === 'object' ? (value?.title ?? '') : '';
    const fieldIsEmpty = isEmpty(url) && isEmpty(title);
    if (required && fieldIsEmpty) return { isValid: false, errorMessage: validation.fieldRequired };
    if (fieldIsEmpty) return VALID;
    if (isEmpty(url) || !isValidUrl(url)) return { isValid: false, errorMessage: validation.enterValidUrl };
    if (isEmpty(title)) return { isValid: false, errorMessage: validation.enterLinkText };
    return VALID;
  }

  case 'input':
  case 'multiLine': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    const maxLength = schema.validation?.maxLength ?? schema.maxLength;
    if (maxLength !== undefined && String(value).length > maxLength) {
      return { isValid: false, errorMessage: validation.maxLength(maxLength) };
    }
    return VALID;
  }

  case 'color': {
    if (required && isEmpty(value)) return { isValid: false, errorMessage: validation.fieldRequired };
    if (isEmpty(value)) return VALID;
    if (!/^#[0-9A-Fa-f]{6}$/.test(String(value).trim())) {
      return { isValid: false, errorMessage: validation.enterValidColor };
    }
    return VALID;
  }

  default:
    return VALID;
  }
};